import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import PdfBadges from '@components/PdfExport/PdfBadges';
import {
  PdfBadge,
  PdfBadgeRow,
  PdfEmptyText,
  PdfSectionCard,
  PdfSectionLabel,
  PdfTable,
  PdfTwoColRow,
} from '@components/PdfExport/PdfExport.styles';
import PdfSection from '@components/PdfExport/PdfSection';

import type {
  PdfAllergy,
  PdfPatientData,
} from '@hooks/useHealthSummaryPdfData';

import { formatDate } from '@utils/format';

import {
  AvatarCircle,
  DirectiveItem,
  DirectiveLabel,
  DirectiveValue,
  GeneratedLabel,
  HeaderLeft,
  HeaderRight,
  HeaderRow,
  InsuranceHeaderRow,
  InsurancePlanNum,
  InsuranceProvider,
  InsuranceRow,
  MedicalCard,
  MedicalCardLabel,
  MedicalCardValue,
  MedicalCardsRow,
  PageFooter,
  PatientMeta,
  PatientName,
  Subsection,
  SubsectionLabel,
} from './HealthSummaryPdfTemplate.style';

const messages = defineMessages({
  generatedOn: {
    id: 'healthSummaryPdf.generatedOn',
    defaultMessage: 'Generated',
  },
  yearsOld: {
    id: 'healthSummaryPdf.yearsOld',
    defaultMessage: 'years old',
  },
  medical: {
    id: 'healthSummaryPdf.medical',
    defaultMessage: 'Medical',
  },
  bloodType: {
    id: 'healthSummaryPdf.bloodType',
    defaultMessage: 'Blood Type',
  },
  height: {
    id: 'healthSummaryPdf.height',
    defaultMessage: 'Height',
  },
  weight: {
    id: 'healthSummaryPdf.weight',
    defaultMessage: 'Weight',
  },
  allergies: {
    id: 'healthSummaryPdf.allergies',
    defaultMessage: 'Allergies',
  },
  medicalConditions: {
    id: 'healthSummaryPdf.medicalConditions',
    defaultMessage: 'Medical Conditions',
  },
  procedures: {
    id: 'healthSummaryPdf.procedures',
    defaultMessage: 'Surgeries & Procedures',
  },
  medications: {
    id: 'healthSummaryPdf.medications',
    defaultMessage: 'Medications',
  },
  immunizations: {
    id: 'healthSummaryPdf.immunizations',
    defaultMessage: 'Immunizations',
  },
  medicationsAndImmunizations: {
    id: 'healthSummaryPdf.medicationsAndImmunizations',
    defaultMessage: 'Medications & Immunizations',
  },
  insurance: {
    id: 'healthSummaryPdf.insurance',
    defaultMessage: 'Insurance',
  },
  directives: {
    id: 'healthSummaryPdf.directives',
    defaultMessage: 'Directives',
  },
  organDonation: {
    id: 'healthSummaryPdf.organDonation',
    defaultMessage: 'Organ Donation',
  },
  bloodProducts: {
    id: 'healthSummaryPdf.bloodProducts',
    defaultMessage: 'Blood Products',
  },
  heroicMeasures: {
    id: 'healthSummaryPdf.heroicMeasures',
    defaultMessage: 'Heroic Measures',
  },
  powerOfAttorney: {
    id: 'healthSummaryPdf.powerOfAttorney',
    defaultMessage: 'Power of Attorney',
  },
  emergencyContacts: {
    id: 'healthSummaryPdf.emergencyContacts',
    defaultMessage: 'Emergency Contacts',
  },
  healthcareContacts: {
    id: 'healthSummaryPdf.healthcareContacts',
    defaultMessage: 'Healthcare Contacts',
  },
  priority: {
    id: 'healthSummaryPdf.priority',
    defaultMessage: 'Priority',
  },
  name: {
    id: 'healthSummaryPdf.name',
    defaultMessage: 'Name',
  },
  phone: {
    id: 'healthSummaryPdf.phone',
    defaultMessage: 'Phone',
  },
  role: {
    id: 'healthSummaryPdf.role',
    defaultMessage: 'Role',
  },
  noAllergies: {
    id: 'healthSummaryPdf.noAllergies',
    defaultMessage: 'No allergies listed',
  },
  noConditions: {
    id: 'healthSummaryPdf.noConditions',
    defaultMessage: 'No conditions listed',
  },
  noProcedures: {
    id: 'healthSummaryPdf.noProcedures',
    defaultMessage: 'No procedures listed',
  },
  noMedications: {
    id: 'healthSummaryPdf.noMedications',
    defaultMessage: 'No medications listed',
  },
  noImmunizations: {
    id: 'healthSummaryPdf.noImmunizations',
    defaultMessage: 'No immunizations listed',
  },
  noInsurance: {
    id: 'healthSummaryPdf.noInsurance',
    defaultMessage: 'No insurance on file',
  },
  noEmergencyContacts: {
    id: 'healthSummaryPdf.noEmergencyContacts',
    defaultMessage: 'No emergency contacts listed',
  },
  noHealthcareContacts: {
    id: 'healthSummaryPdf.noHealthcareContacts',
    defaultMessage: 'No contacts on file',
  },
  unknown: {
    id: 'GLOBAL.unknown',
    defaultMessage: 'Unknown',
  },
  confidentiality: {
    id: 'healthSummaryPdf.confidentiality',
    defaultMessage:
      'Confidential health record — handle in accordance with applicable privacy regulations',
  },
  provider: {
    id: 'healthSummaryPdf.provider',
    defaultMessage: 'Provider',
  },
  planNumber: {
    id: 'healthSummaryPdf.planNumber',
    defaultMessage: 'Plan number',
  },
});

interface HealthSummaryPdfTemplateProps {
  data: PdfPatientData;
}

function getVisibleNoKnownAllergies(
  allergies: PdfAllergy[],
  noKnownAllergies: string[]
): string[] {
  const hasAnyRealAllergy = allergies.length > 0;
  const hasFoodAllergy = allergies.some((allergy) =>
    allergy.categories.some((category) =>
      category.toLowerCase().trim().includes('food')
    )
  );
  const hasDrugAllergy = allergies.some((allergy) =>
    allergy.categories.some((category) => {
      const normalized = category.toLowerCase().trim();
      return normalized.includes('drug') || normalized.includes('medication');
    })
  );

  return noKnownAllergies.filter((label) => {
    const normalizedLabel = label.toLowerCase().trim();

    if (hasAnyRealAllergy && normalizedLabel === 'no known allergies') {
      return false;
    }

    if (hasFoodAllergy && normalizedLabel.includes('food')) {
      return false;
    }

    if (
      hasDrugAllergy &&
      (normalizedLabel.includes('drug') ||
        normalizedLabel.includes('medication'))
    ) {
      return false;
    }

    return true;
  });
}

/**
 * Health Summary PDF content template.
 *
 * Renders the patient-specific content (header, vitals, badges, contacts, etc.)
 * using shared generic PDF components from `@components/PdfExport`.
 *
 * This component is a pure content layer — it does NOT include the A4 page
 * wrapper, print controls, or auto-print logic. Those are handled by
 * `PdfExportPage` in the consuming page (`src/pages/summary/print.tsx`).
 */
export default function HealthSummaryPdfTemplate({
  data,
}: HealthSummaryPdfTemplateProps) {
  const { formatMessage } = useIntl();
  const today = formatDate({ date: new Date(), formatter: 'date' });
  const visibleNoKnownAllergies = getVisibleNoKnownAllergies(
    data.allergies,
    data.noKnownAllergies
  );

  const dobLine = [
    data.dob,
    data.age !== null
      ? `${data.age} ${formatMessage(messages.yearsOld)}`
      : null,
    data.sex,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <>
      {/* ── Patient header ── */}
      <HeaderRow>
        <HeaderLeft>
          <PatientName>
            {data.name ?? formatMessage(messages.unknown)}
          </PatientName>
          {dobLine && <PatientMeta>{dobLine}</PatientMeta>}
          {data.address && <PatientMeta>{data.address}</PatientMeta>}
          {(data.phone || data.email) && (
            <PatientMeta>
              {[data.phone, data.email].filter(Boolean).join(' · ')}
            </PatientMeta>
          )}
        </HeaderLeft>

        <HeaderRight>
          <AvatarCircle>{data.initials}</AvatarCircle>
          <GeneratedLabel>
            {formatMessage(messages.generatedOn)}
            <br />
            {today}
          </GeneratedLabel>
        </HeaderRight>
      </HeaderRow>

      {/* ── Medical vitals card ── */}
      <PdfSectionCard>
        <PdfSectionLabel>{formatMessage(messages.medical)}</PdfSectionLabel>

        <MedicalCardsRow>
          <MedicalCard>
            <MedicalCardValue>
              {data.bloodType ?? formatMessage(messages.unknown)}
            </MedicalCardValue>
            <MedicalCardLabel>
              {formatMessage(messages.bloodType)}
            </MedicalCardLabel>
          </MedicalCard>
          <MedicalCard>
            <MedicalCardValue>
              {data.height ?? formatMessage(messages.unknown)}
            </MedicalCardValue>
            <MedicalCardLabel>
              {formatMessage(messages.height)}
            </MedicalCardLabel>
          </MedicalCard>
          <MedicalCard>
            <MedicalCardValue>
              {data.weight ?? formatMessage(messages.unknown)}
            </MedicalCardValue>
            <MedicalCardLabel>
              {formatMessage(messages.weight)}
            </MedicalCardLabel>
          </MedicalCard>
        </MedicalCardsRow>

        {/* Allergies */}
        <Subsection>
          <SubsectionLabel>{formatMessage(messages.allergies)}</SubsectionLabel>
          {data.allergies.length > 0 || visibleNoKnownAllergies.length > 0 ? (
            <PdfBadgeRow>
              {data.allergies.map((a) => (
                <PdfBadge
                  key={a.name}
                  shape="chip"
                  variant={a.severe ? 'severe' : 'default'}
                >
                  {a.name}
                </PdfBadge>
              ))}
              {visibleNoKnownAllergies.map((a) => (
                <PdfBadge key={a} shape="chip" variant="muted">
                  {a}
                </PdfBadge>
              ))}
            </PdfBadgeRow>
          ) : (
            <PdfEmptyText>{formatMessage(messages.noAllergies)}</PdfEmptyText>
          )}
        </Subsection>

        {/* Conditions */}
        <Subsection>
          <SubsectionLabel>
            {formatMessage(messages.medicalConditions)}
          </SubsectionLabel>
          <PdfBadges
            chip
            emptyText={formatMessage(messages.noConditions)}
            items={data.conditions}
          />
        </Subsection>

        {/* Surgeries & Procedures */}
        <Subsection>
          <SubsectionLabel>
            {formatMessage(messages.procedures)}
          </SubsectionLabel>
          <PdfBadges
            chip
            emptyText={formatMessage(messages.noProcedures)}
            items={data.procedures}
          />
        </Subsection>

        {/* Medications & Immunizations */}
        <Subsection>
          <SubsectionLabel>
            {formatMessage(messages.medicationsAndImmunizations)}
          </SubsectionLabel>
          <PdfBadgeRow>
            {data.medications.length === 0 &&
            data.immunizations.length === 0 ? (
              <>
                <PdfBadge shape="chip" variant="muted">
                  {formatMessage(messages.noMedications)}
                </PdfBadge>
                <PdfBadge shape="chip" variant="muted">
                  {formatMessage(messages.noImmunizations)}
                </PdfBadge>
              </>
            ) : (
              <>
                {data.medications.map((m) => (
                  <PdfBadge key={m} shape="chip" variant="default">
                    {m}
                  </PdfBadge>
                ))}
                {data.immunizations.map((i) => (
                  <PdfBadge key={i} shape="chip" variant="default">
                    {i}
                  </PdfBadge>
                ))}
              </>
            )}
          </PdfBadgeRow>
        </Subsection>
      </PdfSectionCard>

      {/* ── Insurance + Directives ── */}
      <PdfTwoColRow>
        {/* Insurance */}
        <PdfSection label={formatMessage(messages.insurance)}>
          {data.coverage.length === 0 ? (
            <PdfEmptyText>{formatMessage(messages.noInsurance)}</PdfEmptyText>
          ) : (
            <>
              <InsuranceHeaderRow>
                <span>{formatMessage(messages.provider)}</span>
                <span>{formatMessage(messages.planNumber)}</span>
              </InsuranceHeaderRow>
              {data.coverage.map((c, i) => (
                <InsuranceRow key={i}>
                  <InsuranceProvider>{c.provider || '—'}</InsuranceProvider>
                  <InsurancePlanNum>{c.planNumber || '—'}</InsurancePlanNum>
                </InsuranceRow>
              ))}
            </>
          )}
        </PdfSection>

        {/* Directives */}
        <PdfSection label={formatMessage(messages.directives)}>
          <DirectiveItem>
            <DirectiveLabel>
              {formatMessage(messages.organDonation)}
            </DirectiveLabel>
            {data.organDonation.length > 0 ? (
              <DirectiveValue>{data.organDonation.join(' · ')}</DirectiveValue>
            ) : (
              <PdfEmptyText>{formatMessage(messages.unknown)}</PdfEmptyText>
            )}
          </DirectiveItem>

          <DirectiveItem>
            <DirectiveLabel>
              {formatMessage(messages.bloodProducts)}
            </DirectiveLabel>
            {data.bloodProducts.length > 0 ? (
              <DirectiveValue>{data.bloodProducts.join(' · ')}</DirectiveValue>
            ) : (
              <PdfEmptyText>{formatMessage(messages.unknown)}</PdfEmptyText>
            )}
          </DirectiveItem>

          <DirectiveItem>
            <DirectiveLabel>
              {formatMessage(messages.heroicMeasures)}
            </DirectiveLabel>
            {data.heroicMeasures.length > 0 ? (
              <DirectiveValue>{data.heroicMeasures.join(' · ')}</DirectiveValue>
            ) : (
              <PdfEmptyText>{formatMessage(messages.unknown)}</PdfEmptyText>
            )}
          </DirectiveItem>

          {data.powerOfAttorney && (
            <DirectiveItem>
              <DirectiveLabel>
                {formatMessage(messages.powerOfAttorney)}
              </DirectiveLabel>
              <DirectiveValue>
                {[data.powerOfAttorney.name, data.powerOfAttorney.phone]
                  .filter(Boolean)
                  .join(' · ')}
              </DirectiveValue>
            </DirectiveItem>
          )}
        </PdfSection>
      </PdfTwoColRow>

      {/* ── Emergency Contacts ── */}
      <PdfSection label={formatMessage(messages.emergencyContacts)}>
        {data.emergencyContacts.length === 0 ? (
          <PdfEmptyText>
            {formatMessage(messages.noEmergencyContacts)}
          </PdfEmptyText>
        ) : (
          <PdfTable>
            <thead>
              <tr>
                <th>{formatMessage(messages.priority)}</th>
                <th>{formatMessage(messages.name)}</th>
                <th>{formatMessage(messages.phone)}</th>
              </tr>
            </thead>
            <tbody>
              {data.emergencyContacts.map((contact, i) => (
                <tr key={i}>
                  <td>{contact.priority}</td>
                  <td>{contact.name || '—'}</td>
                  <td>{contact.phone || '—'}</td>
                </tr>
              ))}
            </tbody>
          </PdfTable>
        )}
      </PdfSection>

      {/* ── Healthcare Contacts ── */}
      <PdfSection label={formatMessage(messages.healthcareContacts)}>
        {data.healthcareContacts.length === 0 ? (
          <PdfEmptyText>
            {formatMessage(messages.noHealthcareContacts)}
          </PdfEmptyText>
        ) : (
          <PdfTable>
            <thead>
              <tr>
                <th>{formatMessage(messages.role)}</th>
                <th>{formatMessage(messages.name)}</th>
                <th>{formatMessage(messages.phone)}</th>
              </tr>
            </thead>
            <tbody>
              {data.healthcareContacts.map((contact, i) => (
                <tr key={i}>
                  <td>{contact.role || '—'}</td>
                  <td>{contact.name || '—'}</td>
                  <td>{contact.phone || '—'}</td>
                </tr>
              ))}
            </tbody>
          </PdfTable>
        )}
      </PdfSection>

      {/* ── Footer ── */}
      <PageFooter>{formatMessage(messages.confidentiality)}</PageFooter>
    </>
  );
}
